'use client';

import { usePolling } from '@/lib/usePolling';
import { formatCents } from '@/lib/format';
import { Repeat, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface MrrPoint {
  month: string;
  total_cents: number;
  property_count: number;
  partial: boolean;
}

interface MrrData {
  mrr: {
    total_cents: number;
    weekly_cents: number;
    biweekly_cents: number;
    monthly_cents: number;
    bundle_cents: number;
    property_count: number;
    weekly_count: number;
    biweekly_count: number;
    monthly_count: number;
    bundle_count: number;
  };
  trend: MrrPoint[];
  delta: { cents: number; vs_month: string | null };
}

const INITIAL: MrrData = {
  mrr: {
    total_cents: 0, weekly_cents: 0, biweekly_cents: 0, monthly_cents: 0, bundle_cents: 0,
    property_count: 0, weekly_count: 0, biweekly_count: 0, monthly_count: 0, bundle_count: 0,
  },
  trend: [],
  delta: { cents: 0, vs_month: null },
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-05-01' -> 'May' */
function monthLabel(monthIso: string): string {
  const m = Number(monthIso.slice(5, 7));
  return MONTH_NAMES[m - 1] ?? '';
}

function Sparkline({ points }: { points: MrrPoint[] }) {
  if (points.length < 2) return null;
  const max = Math.max(...points.map(p => p.total_cents), 1);
  return (
    <div className="flex items-end gap-0.5 h-8" aria-hidden>
      {points.map((p) => {
        const pct = Math.max(6, Math.round((p.total_cents / max) * 100));
        return (
          <div
            key={p.month}
            title={`${monthLabel(p.month)}: ${formatCents(p.total_cents, { decimals: 0, locale: true })}`}
            className={`flex-1 rounded-sm transition-[height] duration-700 ease-out ${
              p.partial ? 'bg-zinc-600' : 'bg-emerald-500/70'
            }`}
            style={{ height: `${pct}%` }}
          />
        );
      })}
    </div>
  );
}

export function RevenueWidget() {
  const { data, isLoading } = usePolling<MrrData>({
    url: '/api/admin/revenue/mrr',
    intervalMs: 300_000,
    initialValue: INITIAL,
  });

  const { mrr, trend, delta } = data;
  const up = delta.cents > 0;
  const down = delta.cents < 0;
  const DeltaIcon = up ? TrendingUp : down ? TrendingDown : Minus;
  const deltaColor = up ? 'text-emerald-400' : down ? 'text-rose-400' : 'text-zinc-500';

  if (isLoading && mrr.total_cents === 0 && trend.length === 0) {
    return <div className="h-[120px] rounded-xl border border-white/5 bg-zinc-900/40 animate-pulse" />;
  }

  return (
    <div className="rounded-xl border border-white/5 bg-zinc-900/60 backdrop-blur-sm px-4 py-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-md bg-zinc-800/60">
            <Repeat className="w-3.5 h-3.5 text-zinc-400" />
          </div>
          <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">
            Recurring MRR
          </span>
        </div>
        {delta.vs_month && (
          <div className={`flex items-center gap-1 ${deltaColor}`}>
            <DeltaIcon className="w-3 h-3" />
            <span className="text-[10px] font-semibold tabular-nums">
              {up ? '+' : down ? '−' : ''}
              {formatCents(Math.abs(delta.cents), { decimals: 0, locale: true })}
              <span className="text-zinc-500 font-normal"> vs {monthLabel(delta.vs_month)}</span>
            </span>
          </div>
        )}
      </div>

      {/* Headline */}
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-2xl font-bold text-zinc-100 tabular-nums leading-none">
          {formatCents(mrr.total_cents, { decimals: 0, locale: true })}
        </span>
        <span className="text-[10px] text-zinc-500">in-season run-rate</span>
      </div>

      {/* Trend */}
      <Sparkline points={trend} />

      {/* Composition */}
      <div className="mt-2 min-w-0 pt-2 border-t border-white/5 text-[10px] text-zinc-400 tabular-nums">
        {mrr.weekly_count} weekly
        <span className="text-zinc-600"> · </span>
        {mrr.biweekly_count} biwk
        <span className="text-zinc-600"> · </span>
        {mrr.bundle_count} bundle
        <span className="text-zinc-600"> · </span>
        {mrr.property_count} accts
      </div>
    </div>
  );
}
